/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, Fragment, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback, useRef } from 'preact/hooks';
import { useClearIcon } from '../hooks/UNSAFE_useClearIcon';
import { FocusableHandle, useFocusableTextField } from '../hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useHover } from '../hooks/UNSAFE_useHover';
import { CountUnit, useLengthFilter } from '../hooks/UNSAFE_useLengthFilter';
import { useTextField } from '../hooks/UNSAFE_useTextField';
import { Label } from '../UNSAFE_Label';
import { usePrefixSuffix } from '../hooks/UNSAFE_usePrefixSuffix';

import {
  ReadonlyTextField,
  ReadonlyTextFieldInput,
  TextField,
  TextFieldInput
} from '../UNSAFE_TextField';
import {
  CompactUserAssistance,
  InlineUserAssistance,
  UserAssistanceDensityType
} from '../UNSAFE_UserAssistance';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { beforeVNode } from '../utils/UNSAFE_componentUtils';
import { ClearIcon } from './ClearIcon';
import { MaxLengthLiveRegion } from '../UNSAFE_TextField';
import { Size } from '../utils/UNSAFE_size';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { useCurrentValueReducer } from '../hooks/UNSAFE_useCurrentValueReducer';

type TextFieldProps = ComponentProps<typeof TextField>;
type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;
type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props = {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: TextFieldInputProps['aria-describedby'];

  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];

  /**
   * Dictates component's autocomplete state.
   * This attribute indicates whether the value of the control can be automatically completed by the browser.
   * The common values are "on" and "off".
   */
  autoComplete?: TextFieldInputProps['autoComplete'];

  /**
   * Autofocus specifies whether the component will get focus when the page is loaded.
   * If it is set to true then the associated component will get input focus when the page is loaded.
   * Setting this property doesn't set the focus to the component: it tells the browser to focus
   * to it when the element is inserted in the document.
   */
  autoFocus?: TextFieldInputProps['autoFocus'];

  /**
   * The content to be rendered after the input field, typically a button.
   */
  endContent?: TextFieldProps['endContent'];

  /**
   * Specifies whether the clear icon should be shown.
   * <ul>
   * <li><code>'always'</code>: The clear icon is always visible.</li>
   * <li><code>'conditionally'</code>: The clear icon is visible under the following conditions:
   * if the component has a non-empty value, and it either has focus or the mouse is over the field.</li>
   * <li><code>'never'</code>: The clear icon is never visible.</li>
   * </ul>
   */
  hasClearIcon?: 'always' | 'conditionally' | 'never';

  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];

  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];

  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;

  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;

  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * committed by the user. Setting this property to <code>true</code> means that a value
   * is required to be committed by the user.
   */
  isRequired?: TextFieldInputProps['isRequired'];

  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has committed a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];

  /**
   * Specifies the label associated with the field.
   */
  label: string;

  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';

  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;

  /**
   * Specifies the maximum number of characters that can be entered in the input field.
   */
  maxLength?: number;

  /**
   * Specifies how the input text characters are to be counted when `maxLength` is specified.
   */
  maxLengthUnit?: CountUnit;

  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];

  /**
   * A short hint to display before the user enters a value.
   */
  placeholder?: TextFieldInputProps['placeholder'];

  /**
   * The prefix displays as regular text before the input text.
   * The prefix is not editable or focusable.
   */
  prefix?: string;

  /**
   * Specifies the role attribute for the input field.
   */
  role?: TextFieldInputProps['role'];

  /**
   * The content to be rendered before the input field, typically an icon.
   */
  startContent?: TextFieldProps['startContent'];

  /**
   * The suffix displays as regular text after the input text.
   * The suffix is not editable or focusable.
   */
  suffix?: string;

  /**
   * Specifies how to align text within the field.
   */
  textAlign?: TextFieldInputProps['textAlign'];

  /**
   * Specifies the density of the user assistance presentation. It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline. Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * The current display value of the component.
   */
  value?: TextFieldInputProps['value'];

  /**
   * The style variant of the component.
   */
  variant?: TextFieldInputProps['variant'];

  /**
   * The type of virtual keyboard to display for entering a value on mobile browsers.
   * This property has no effect on desktop browsers.
   */
  virtualKeyboard?: 'auto' | 'email' | 'number' | 'search' | 'tel' | 'text' | 'url';

  /**
   * Callback invoked when the user commits the entered value by either blurring or pressing Enter.
   */
  onCommit?: TextFieldInputProps['onCommit'];

  /**
   * Callback invoked each time the user changes the value of the field. For example, if the user
   * types 'abcd', this callback will be called four times with the value as 'a', 'ab', 'abc' and 'abcd'
   * respectively.
   */
  onInput: TextFieldInputProps['onInput'];
};

export const InputText = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      autoComplete = 'off',
      autoFocus = false,
      hasClearIcon = 'never',
      endContent,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired = false,
      isRequiredShown,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      maxLength,
      maxLengthUnit,
      messages,
      placeholder,
      prefix,
      role,
      startContent,
      suffix,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      variant = 'default',
      virtualKeyboard,
      onInput,
      onCommit
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const { currentCommitValue, dispatch } = useCurrentValueReducer({ value });
    const onInputAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        // Should dispatch happen first? This will queue up a re-render, ordering should not cause issues (this is async)
        dispatch({ type: 'input', payload: detail.value });
        onInput?.(detail);
      },
      [onInput, dispatch]
    );
    const onCommitAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        // Should dispatch happen first? This will queue up a re-render, ordering should not cause issues (this is async)
        dispatch({ type: 'commit', payload: detail.value });
        onCommit?.(detail);
      },
      [onCommit, dispatch]
    );
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const { enabledElementRef, focusProps, isFocused, readonlyElementRef } = useFocusableTextField<
      HTMLInputElement,
      HTMLDivElement
    >({ isDisabled, isReadonly, ref });
    const { hoverProps, isHover } = useHover({ isDisabled: isReadonly || isDisabled || false });
    const {
      baseId,
      formFieldContext,
      inputProps,
      labelProps,
      textFieldProps,
      userAssistanceProps
    } = useTextField({
      ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isFocused,
      isReadonly,
      isRequiredShown,
      labelEdge,
      messages,
      styleVariant: variant,
      userAssistanceDensity,
      value
    });

    const onClickClearIcon = useCallback(() => {
      // Clicking the clear icon should put the focus on the input field
      enabledElementRef.current?.focus();
      // Send an event to clear the field's value
      onInputAndDispatch?.({ previousValue: value, value: '' });
    }, [onInputAndDispatch, value, enabledElementRef]);

    const maybeClearIcon = useClearIcon({
      clearIcon: <ClearIcon onClick={onClickClearIcon} />,
      display: hasClearIcon,
      hasValue: formFieldContext.hasValue,
      isFocused,
      isEnabled: !isReadonly && !isDisabled,
      isHover
    });
    const endContentCombined = beforeVNode(endContent, maybeClearIcon);

    const {
      renderedPrefix,
      renderedSuffix,
      text: valuePrefixSuffix,
      ariaLabelledBy
    } = usePrefixSuffix({
      baseId,
      hasEndContent: endContent !== undefined,
      hasInsideLabel: label !== undefined && labelEdge === 'inside',
      hasStartContent: startContent !== undefined,
      hasValue: formFieldContext.hasValue,
      isDisabled,
      isFocused,
      labelId: labelProps.id,
      prefix,
      suffix,
      value
    });

    // Handle length filter for user-typed inputs and controlled inputs
    const { isMaxLengthExceeded, valueLength, onFilteredInput } = useLengthFilter({
      maxLength,
      maxLengthUnit,
      value,
      onInput: onInputAndDispatch,
      onCommit: onCommitAndDispatch
    });

    const labelComp = labelEdge !== 'none' ? <Label {...labelProps}>{label}</Label> : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled || isReadonly ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled or readonly fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const anchorRef = useRef<HTMLDivElement>(null);

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={anchorRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    if (isReadonly) {
      // TODO: should be able to configure whether start/end content is shown when readonly
      // JET-49916 - Preact InputText: show start/end content when readonly
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            onBlur={focusProps.onfocusout}
            onFocus={focusProps.onfocusin}
            ref={anchorRef}
            {...fieldLabelProps}>
            <ReadonlyTextFieldInput
              aria-describedby={inputProps['aria-describedby']}
              aria-label={ariaLabel}
              aria-labelledby={labelProps.id}
              as="div"
              autoFocus={autoFocus}
              elementRef={readonlyElementRef}
              textAlign={textAlign}
              value={valuePrefixSuffix}
              hasEmptyLabel={label === '' && labelEdge === 'none'}
              hasInsideLabel={label !== undefined && labelEdge === 'inside'}
            />
          </ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    const mainContent = (
      <Fragment>
        {renderedPrefix}
        <TextFieldInput
          aria-labelledby={ariaLabelledBy}
          aria-label={ariaLabel}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          currentCommitValue={currentCommitValue}
          hasEmptyLabel={label === '' && labelEdge === 'none'}
          hasEndContent={endContent !== undefined}
          hasInsideLabel={labelComp !== undefined && labelEdge === 'inside'}
          hasPrefix={renderedPrefix !== undefined}
          hasStartContent={startContent !== undefined}
          hasSuffix={renderedSuffix !== undefined}
          inputRef={enabledElementRef}
          isRequired={isRequired}
          placeholder={placeholder}
          role={role}
          textAlign={textAlign}
          type={isMobile() ? virtualKeyboard : undefined}
          value={isDisabled ? valuePrefixSuffix : value}
          onCommit={onCommitAndDispatch}
          onInput={onFilteredInput}
          {...inputProps}
        />
        {renderedSuffix}
        {maxLength !== undefined && (
          <MaxLengthLiveRegion {...{ isMaxLengthExceeded, maxLength, valueLength }} />
        )}
      </Fragment>
    );

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <TextField
          endContent={endContentCombined}
          compactUserAssistance={compactUserAssistance}
          inlineUserAssistance={inlineUserAssistance}
          mainContent={mainContent}
          onBlur={focusProps.onfocusout}
          onFocus={focusProps.onfocusin}
          startContent={startContent}
          mainFieldRef={anchorRef}
          {...textFieldProps}
          {...fieldLabelProps}
          {...hoverProps}
        />
      </FormFieldContext.Provider>
    );
  }
);

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if runnning on a mobile device, false otherwise
 */
function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}
